import React, { useState, useEffect, useMemo } from 'react'
import {
	Dimensions,
	View,
	KeyboardAvoidingView,
	ScrollView,
	Platform,
	TouchableOpacity,
	Image,
} from 'react-native'
import Geocoder from 'react-native-geocoding'
import { Text } from '../../../components/Themed'
import { DisplayOrder } from '../../../components/OrderCart/DisplayOrder'
import { Spacer } from '../../../components/Spacer'
import GlobalStyle, {
	MAX_CHECKOUT_WIDTH,
	mediumTextSize,
	titleTextSize,
	IS_LANDSCAPE,
	UNDER_MIN_HEIGHT,
	largeTextSize,
	SMALL_WIDTH,
} from '../../../constants/GlobalStyle'
import { styles } from '../Helpers/style'
import Colors from '../../../constants/Colors'
import ScheduleOrder, { combineHours } from '../../../components/ScheduleHours/ScheduleOrderModal'
import { CartHeader } from './Header'
import CartFooter, { KioskCartFooter } from './Footer'
import { NotesInput } from './NotesInput'
import NavFooter from '../../../components/Footers/NavFooter'
import {
	PickupTimeBox,
	RewardsBox,
	ChangeLocationBox,
	DeliveryDetailsBox,
	PickupPointDetailsBox,
} from './SelectorBox'
import { responsiveHeight, responsiveWidth } from 'react-native-responsive-dimensions'
import { auth } from '../../../firebase/config'
import { WebHeader } from '../../../components/Headers/WebHeader'
import CopyrightFooter from '../../../components/Footers/CopyrightFooter'
import { logRemoveFromCartAnalytics } from '../../../firebase/analytics'
import { LocationSelectModal } from '../../../components/LocationSelect'
import POSNavBar from '../../../navigation/POSNavBar'
import useStripeDigitalWallet from '../Hooks/useStripeDigitalWallet.web'
import DeliveryPickupToggle from '../../../components/Delivery/DeliveryPickupToggle'
import OrderBanner from '../../../components/Delivery/OrderBanner'
import Banner from '../../../components/Banner'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { prepText } from '../../../helpers/prepText'
import {
	areAllRestaurantsInCartOpen,
	isWithinOrgHours,
	itemAvailabilityCheck,
} from '../../../helpers/restaurantHours'
import {
	removeItemFromCart,
	setPersistedState,
	resetDeliveryEstimate,
	resetCouponState,
} from '../../../state/Slices/persistedSlice'
import { setCheckoutState } from '../../../state/Slices/checkoutSlice'
import NoItemsInCart from './NoItems'
import CheckoutModal from '../Modals/CheckoutModal'
import { showAlert } from '../../../state/Slices/settingsSlice'
import Formatter from '../../../helpers/Formatter'
import { setShowDeliveryModal } from '../../../state/Slices/dataSlice'
import ExitIcon from '../../../components/Headers/UIButtons'
import { AddMoreItemsButton } from './AddMoreItems'
import CheckoutColumns from './ColumnLayout'
import { AnalyticsEvent, captureEvent } from '../../../helpers/analytics'
import { useGetTimeHoursDifference } from '../../../hooks/useGetTimeDifference'
import { useOrganizationStyles } from '../../../hooks/useOrganizationStyles'

import { colors } from '../../../components/DesignSystem'
import { useEmptyCart } from '../../../hooks/useEmptyCart'
import { useIsFreeDeliveryEligible } from '../Hooks/useIsFreeDeliveryEligible'
import OrderConfirmedModal from '../Modals/OrderConfirmedModal'

export const INVALID_ADDRESS_TEXT =
	"Oops! We're unable to deliver to that address. Please try a different address."
export const NO_ADDRESS_TEXT = 'Please enter your delivery address to checkout.'

export function CheckoutView({
	navigation,
	priceSet,
	getSchedInterval,
	tipFuncData,
	promoFuncData,
	togglePromoModal,
	toggleConfirmModal,
	togglePaymentMethodModal,
}) {
	const dispatch = useAppDispatch()
	const [isOrderConfirmedModalVisible, setIsOrderConfirmedModalVisible] = useState(false)
	const {
		items,
		orderPlaced,
		bagFeeEnabled,
		bagFee,
		orderNote,
		loading,
		isRestaurantSnoozed,
		deliveryAmountToSpend,
		prices,
		showInvalidAddress,
	} = useAppSelector(state => state.checkout)
	const {
		delivery,
		deliveryEnabled,
		paymentProvider,
		allowOrdersOutsideOrgHours,
		localTimezone,
		outsideOrgHoursOrderType,
		address: orgAddress,
	} = useAppSelector(state => state.settings)
	const { loadedData, restaurants, organizationLogo, restaurantList } = useAppSelector(
		state => state.data
	)
	const {
		cart,
		cartPrep,
		selectedLocation,
		isDelivery,
		deliveryEstimate,
		deliveryEstimateList,
		redeemedRewards,
		pickupPointModalVisible,
		couponCode,
	} = useAppSelector(state => state.persisted)
	const { paymentMethods, userLocation } = useAppSelector(state => state.user)
	const timeHoursDifference = useGetTimeHoursDifference()
	const { buttonAlternateColor } = useOrganizationStyles()
	const emptyCart = useEmptyCart()

	const { isFreeDeliveryEligible } = useIsFreeDeliveryEligible()
	const hasSelectedAddress = useMemo(() => !!userLocation?.longAddress, [userLocation])
	const hasInvalidAddress = useMemo(() => {
		return (
			isDelivery &&
			(deliveryEstimate.estimate.totalFee == -1 || deliveryEstimate.estimate.timeToDeliver == -1)
		)
	}, [isDelivery, deliveryEstimate.estimate.totalFee, deliveryEstimate.estimate.timeToDeliver])

	const addressWarningText = useMemo<string>(() => {
		if (isDelivery) {
			if (!hasSelectedAddress) return NO_ADDRESS_TEXT
			if (hasInvalidAddress) return INVALID_ADDRESS_TEXT
		}

		dispatch(
			setCheckoutState({
				showInvalidAddress: false,
			})
		)
		return undefined
	}, [hasSelectedAddress, hasInvalidAddress, userLocation])

	const orgLogoDark = loadedData[global.org].logoDark ? loadedData[global.org].logoDark : ''

	let deliveryEstimateForRestaurant: any = deliveryEstimate

	if (
		deliveryEstimate &&
		cart &&
		deliveryEstimateList?.length > 0 &&
		cart.length > 0 &&
		!loadedData[global.org].singleBrandEnabled
	) {
		const estimate = deliveryEstimateList.find(est => est.restaurantId === cart[0].rId)
		if (estimate) {
			deliveryEstimateForRestaurant = estimate
		}
	}

	const deliveryTime =
		deliveryEstimateForRestaurant && deliveryEstimateForRestaurant.estimate
			? deliveryEstimateForRestaurant.estimate.timeToDeliver
			: 60

	const [showOrderNotePopup, setShowOrderNotePopup] = useState(false)

	const [locationsModalVisible, setLocationsModalVisible] = useState(false)

	const hoursRef = React.useRef<any>()

	const descriptor = global.pos ? 'the' : 'your'

	const singleBrandEnabled = loadedData[global.org].singleBrandEnabled || false

	let addressSpace = ''
	const address =
		selectedLocation && selectedLocation.address
			? selectedLocation.address
			: { addressLine1: '', addressLine2: '' }
	if (address.addressLine2 !== '') {
		addressSpace = ' '
	}
	const formattedAddressLine = `${address.addressLine1} ${
		address.addressLine2 ? address.addressLine2 : ''
	}`

	const rIdToken = cart.length > 0 ? cart[0].rId : selectedLocation ? selectedLocation.id : null
	if (Platform.OS === 'web') {
		useStripeDigitalWallet()
	}

	useEffect(() => {
		if (isDelivery && (!userLocation || !userLocation.longAddress) && deliveryEnabled) {
			dispatch(setShowDeliveryModal(true))
		}
	}, [isDelivery])

	useEffect(() => {
		if (orderPlaced) {
			setIsOrderConfirmedModalVisible(true)
		}
	}, [orderPlaced])

	const MAX_WIDTH = 1024

	const [useColumnView, setUseColumnView] = useState(
		Platform.OS === 'web' && Dimensions.get('window').width > MAX_WIDTH
	)

	useEffect(() => {
		if (Platform.OS !== 'web') return

		const handleResize = () => {
			setUseColumnView(window.innerWidth > MAX_WIDTH)
		}

		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	useEffect(() => {
		if (!userLocation.longAddress) {
			dispatch(resetDeliveryEstimate())
		}
	}, [userLocation.longAddress])

	useEffect(() => {
		if (items.length === 0) {
			dispatch(resetCouponState())
		}
	}, [items])

	function removeItem(item) {
		captureEvent(AnalyticsEvent.RemoveItem, {
			itemId: item.id,
			itemName: item.name,
			itemLocationId: item.rId,
			quantity: item.qty,
			price: (item.ppu * item.qty) / 100,
		})

		logRemoveFromCartAnalytics((item.ppu * item.qty) / 100, {
			item_id: item.id,
			item_name: item.name,
			item_location_id: item.rId,
			quantity: item.qty,
			price: (item.ppu * item.qty) / 100,
			item_category: item.menuItemCategory,
		})

		// Check if loyalty reward is being removed
		if (item.appliedCoupon) {
			//remove from redeemedRewards
			const newRewards = redeemedRewards.filter(x => x.rewardId !== item.appliedCoupon)
			dispatch(setPersistedState({ redeemedRewards: newRewards }))
		}
		if (item.isFree) {
			dispatch(setCheckoutState({ isFreeItemDiscount: false }))
		}
		dispatch(
			setCheckoutState({
				items: items.filter(x => {
					return !(x.id === item.id && x.idempotencyKey === item.idempotencyKey)
				}),
			})
		)

		dispatch(removeItemFromCart(item.id, item.idempotencyKey))

		if (cart.length < 1) {
			emptyCart()
			if (Platform.OS === 'web') {
				navigation.replace('HomeStack', { screen: 'RestaurantSelect' })
			} else {
				navigation.reset({
					index: 0,
					routes: [{ name: 'HomeStack' }],
				})
			}
		} else {
			navigation.navigate('TabCheckout')
		}
	}

	const setPickupPointModalVisible = value => {
		dispatch(setPersistedState({ pickupPointModalVisible: value }))
	}

	const cartOpen = areAllRestaurantsInCartOpen(
		cart,
		restaurants,
		loadedData[global.org].hours ?? null,
		allowOrdersOutsideOrgHours,
		localTimezone
	)

	const PickupDisplay = () => {
		return (
			<TouchableOpacity
				style={{ paddingTop: 10 }}
				onPress={() => {
					hoursRef.current.open()
				}}
			>
				<Text
					style={{
						color: Colors.greyscale[5],
						fontSize: mediumTextSize,
					}}
				>
					{prepText(cartPrep, isDelivery)}
				</Text>
			</TouchableOpacity>
		)
	}

	const freeDeliveryPriceForSameCity = delivery.freeDeliveryMinimumOrder / 100
	const freeDliveryPriceForOutsideCity =
		deliveryEstimateForRestaurant && deliveryEstimateForRestaurant.estimate
			? Math.round(
					deliveryEstimateForRestaurant.estimate.fee * delivery.freeDeliveryMultiplier +
						delivery.minSpend
				) / 100
			: -1

	const freeDeliveryPrice =
		isFreeDeliveryEligible && !!delivery.freeDeliveryMinimumOrder
			? freeDeliveryPriceForSameCity
			: freeDliveryPriceForOutsideCity

	const filteredRestaurantsByItems = restaurantList.filter(restaurant =>
		items.some(item => item.rId === restaurant.id)
	)

	function cartPress() {
		const restaurantIds = [...new Set(cart.map(item => item.rId))]
		const filteredRestaurants = restaurants.filter(r => restaurantIds.includes(r.id))
		const canPayNowForScheduled = cartPrep.isScheduled
			? itemAvailabilityCheck(
					cart,
					loadedData[global.org].hours ?? null,
					false,
					localTimezone,
					isDelivery ? new Date(cartPrep.deliveryTime) : new Date(cartPrep.pickupTime),
					combineHours(
						filteredRestaurants,
						loadedData[global.org].hours ?? null,
						loadedData[global.org].allowOrdersOutsideOrgHours ?? false
					),
					timeHoursDifference
				)
			: true
		const canAddToCart =
			loadedData[global.org].singleBrandEnabled || !deliveryEnabled || !isDelivery ? true : true //canCheckoutForDelivery()

		const hasMultipleRestaurants = restaurantIds.length > 1

		if (hasMultipleRestaurants && outsideOrgHoursOrderType === 'single') {
			const withinOrgHours = isWithinOrgHours(loadedData[global.org].hours, localTimezone)
			if (!withinOrgHours) {
				dispatch(
					showAlert({
						title: 'Orders from multiple restaurants unavailable',
						body: 'Please remove items from your cart to continue with checkout.',
					})
				)
				return
			}
		}
		if (!canAddToCart) {
			dispatch(
				showAlert({
					title: 'Error processing your delivery',
					body: 'Please check your cart and try again.',
				})
			)
		} else if (!canPayNowForScheduled) {
			dispatch(
				showAlert({
					title: 'Items in your cart are not available at this time',
					body: 'Please choose a different time to receive your order.',
				})
			)
		} else if (
			(prices.deliveryFee === -1 || deliveryTime === -1) &&
			isDelivery &&
			userLocation?.longAddress === undefined
		) {
			dispatch(setShowDeliveryModal(true))
		} else if (global.kiosk || cartOpen || cartPrep.isScheduled) {
			toggleConfirmModal()
		} else if (isDelivery && deliveryAmountToSpend !== 0 && !isNaN(deliveryAmountToSpend)) {
			dispatch(
				showAlert({
					title: `Add ${Formatter.currencyFormat(deliveryAmountToSpend / 100)} more to your cart!`,
					body: `Delivery requires a minimum order of ${Formatter.currencyFormat(delivery.minSpend / 100)}.`,
				})
			)
		} else {
			if (hoursRef && hoursRef.current) {
				hoursRef.current.open()
			}
		}
	}

	return (
		<>
			{orderPlaced ? (
				<>
					<View style={styles.fullScreen}></View>
				</>
			) : (
				<View></View>
			)}

			<OrderBanner navigation={navigation} />

			{/* modal (confirm order) code */}
			{!orderPlaced ? (
				<CheckoutModal
					toggle={toggleConfirmModal}
					navigation={navigation}
					promoFuncData={promoFuncData}
					prices={prices}
					tipFunctions={tipFuncData}
				/>
			) : (
				<OrderConfirmedModal
					// toggle={toggleConfirmModal}
					// navigation={navigation}
					// promoFuncData={promoFuncData}
					// prices={prices}
					// tipFunctions={tipFuncData}
					setIsOrderConfirmedModalVisible={setIsOrderConfirmedModalVisible}
					visible={isOrderConfirmedModalVisible}
				/>
			)}

			{filteredRestaurantsByItems && <ScheduleOrder hoursRef={hoursRef} />}
			{singleBrandEnabled && (
				<LocationSelectModal
					visible={locationsModalVisible}
					setVisible={setLocationsModalVisible}
					navigation={navigation}
					rIdToken={rIdToken}
					isCheckout={true}
				/>
			)}

			{Platform.OS === 'web' && !orderPlaced && !UNDER_MIN_HEIGHT && (
				<WebHeader logo={organizationLogo} navigation={navigation} />
			)}

			{global.kiosk && (
				<ExitIcon
					onPress={() => {
						if (singleBrandEnabled && global.kiosk) {
							navigation.navigate('HomeStack', {
								screen: 'Menu',
								params: { rId: rIdToken },
							})
						} else {
							navigation.navigate('HomeStack', { screen: 'RestaurantSelect' })
						}
					}}
				/>
			)}

			<KeyboardAvoidingView
				behavior={'height'}
				style={[GlobalStyle.checkoutContainer, { paddingHorizontal: 0 }]}
			>
				{/* default view */}
				{Platform.OS === 'web' && !orderPlaced && UNDER_MIN_HEIGHT && useColumnView && (
					<WebHeader logo={organizationLogo} navigation={navigation} />
				)}
				<CheckoutColumns
					prices={prices}
					useColumnView={useColumnView && !global.kiosk}
					isRestaurantSnoozed={isRestaurantSnoozed}
					onCartPress={cartPress}
					deliveryAmountToSpend={deliveryAmountToSpend}
					hasInvalidAddress={hasInvalidAddress}
					hasSelectedAddress={hasSelectedAddress}
					navigation={navigation}
					singleBrandEnabled={singleBrandEnabled}
					restaurantList={restaurantList}
					setLocationsModalVisible={setLocationsModalVisible}
					locationsModalVisible={locationsModalVisible}
					formattedAddressLine={formattedAddressLine}
					pickupPointModalVisible={pickupPointModalVisible}
					setPickupPointModalVisible={setPickupPointModalVisible}
					filteredRestaurantsByItems={filteredRestaurantsByItems}
					hoursRef={hoursRef}
				>
					<ScrollView>
						{Platform.OS === 'web' && !orderPlaced && UNDER_MIN_HEIGHT && !useColumnView && (
							<WebHeader logo={organizationLogo} navigation={navigation} />
						)}
						{Platform.OS !== 'web' && (
							<>
								<View
									style={{
										backgroundColor: colors.grayTranslucent,
										height: 400,
										marginTop: -400 + responsiveHeight(20),
									}}
								></View>
								<CartHeader organizationLogo={orgLogoDark ? orgLogoDark : organizationLogo} />
							</>
						)}

						<View
							style={{
								marginBottom: 32,
								alignSelf: 'center',
								width: '90%',
								maxWidth: MAX_CHECKOUT_WIDTH,
								marginHorizontal: '5%',
								display: cart.length ? 'flex' : 'none',
							}}
						>
							<View
								style={{
									paddingTop: Platform.OS === 'web' ? (!useColumnView ? 20 : 70) : 30,
								}}
							>
								<View
									style={{
										flexDirection: 'row',
										justifyContent: 'space-between',
										paddingBottom: global.pos ? 0 : 25,
										paddingTop: 0,
									}}
								>
									<Text
										style={[
											GlobalStyle.titleText,
											{
												paddingBottom: 0,
												color: global.orgColor2,
											},
										]}
									>
										{global.pos ? 'ORDER SUMMARY' : 'MY ORDER'}
									</Text>

									<View
										style={{
											paddingTop: 5,
											display:
												!loadedData[global.org].singleBrandEnabled && Platform.OS === 'web'
													? 'none'
													: 'flex',
										}}
									>
										<DeliveryPickupToggle showChange={false} showAddress={false} />
									</View>
								</View>
								{!userLocation.loading && (
									<>
										{isDelivery &&
										deliveryAmountToSpend === 0 &&
										delivery.freeDeliveryMultiplier !== -1 &&
										!isNaN(deliveryAmountToSpend) &&
										!isNaN(delivery.freeDeliveryMultiplier) &&
										deliveryEstimateForRestaurant &&
										deliveryEstimateForRestaurant.estimate &&
										deliveryEstimateForRestaurant.estimate.fee > 0 &&
										freeDeliveryPrice - prices.subTotal > 0 ? (
											<Banner
												text={`Add $${(freeDeliveryPrice - prices.subTotal).toFixed(
													2
												)} to your cart to get free delivery!`}
												showAddItemsButton={true}
												navigation={navigation}
												style={{ marginBottom: 20 }}
											/>
										) : isDelivery &&
										  deliveryAmountToSpend === 0 &&
										  delivery.freeDeliveryMultiplier !== -1 &&
										  !isNaN(deliveryAmountToSpend) &&
										  !isNaN(delivery.freeDeliveryMultiplier) &&
										  deliveryEstimateForRestaurant &&
										  deliveryEstimateForRestaurant.estimate &&
										  deliveryEstimateForRestaurant.estimate.fee > 0 ? (
											<Banner
												text={`Congratulations! You've earned free delivery on your order!`}
												style={{ marginBottom: 20 }}
											/>
										) : (
											<></>
										)}
									</>
								)}

								{global.pos && <PickupDisplay />}
								<Text
									style={{
										fontSize: titleTextSize - 6,
										fontWeight: '300',
										color: Colors.greyscale[10],
										paddingVertical: IS_LANDSCAPE ? '2.5%' : '5%',
										display: global.kiosk ? 'flex' : 'none',
									}}
								>
									READY TO CHECKOUT?
								</Text>
							</View>

							<View>
								<View style={{ display: items.length > 0 ? 'flex' : 'none' }}>
									<Text
										style={[{ marginTop: 10, marginBottom: 15, fontWeight: 'bold', fontSize: 16 }]}
									>
										{'ORDER ITEMS'}
									</Text>
									<View style={{ marginHorizontal: -15 }}>
										<DisplayOrder
											items={items}
											prices={prices}
											isCheckout={true}
											isConfirmed={false}
											navigation={navigation}
											deletePopup={removeItem}
											loadedData={loadedData}
											trackingURL={null}
										/>
									</View>
									<Spacer size={10} />
									<AddMoreItemsButton navigation={navigation} />
								</View>
								<View
									style={{
										display: items.length <= 0 && cart.length > 0 ? 'flex' : 'none',
									}}
								>
									<Image
										style={[
											styles.loadImg,
											{
												width: global.kioskV2 ? 120 : global.kiosk ? 180 : 100,
												height: global.kioskV2 ? 120 : global.kiosk ? 180 : 100,
											},
										]}
										source={require('../../../assets/images/loadImg.gif')}
									/>
								</View>

								<Spacer size={2} />
							</View>
						</View>

						{!global.kiosk && cart.length > 0 && !useColumnView && loadedData && (
							<View style={useColumnView && { marginTop: 16 }}>
								<CartFooter
									prices={prices}
									showBanner={
										isDelivery && deliveryAmountToSpend !== 0 && !isNaN(deliveryAmountToSpend)
									}
									onCartPress={cartPress}
									hasInvalidAddress={hasInvalidAddress}
									hasSelectedAddress={hasSelectedAddress}
									navigation={navigation}
									singleBrandEnabled={singleBrandEnabled}
									restaurantList={restaurantList}
									setLocationsModalVisible={setLocationsModalVisible}
									locationsModalVisible={locationsModalVisible}
									formattedAddressLine={formattedAddressLine}
									pickupPointModalVisible={pickupPointModalVisible}
									setPickupPointModalVisible={setPickupPointModalVisible}
									filteredRestaurantsByItems={filteredRestaurantsByItems}
									hoursRef={hoursRef}
								/>
							</View>
						)}
						{Platform.OS === 'web' && cart.length > 0 && !orderPlaced && !useColumnView && (
							<>
								<CopyrightFooter />
								{/* <PickupTimeBox
									toggleTimeModal={() => {
										hoursRef.current.open()
									}}
									pickupText={prepText(cartPrep, isDelivery)}
									isOpen={cartOpen}
								/> */}
							</>
						)}
					</ScrollView>
				</CheckoutColumns>
				{Platform.OS === 'web' && cart.length > 0 && !orderPlaced && useColumnView && (
					<CopyrightFooter />
				)}
			</KeyboardAvoidingView>
			{global.kiosk && cart.length > 0 && (
				<>
					<KioskCartFooter
						prices={prices}
						cost={bagFee.totalAmount}
						priceSet={priceSet}
						couponCode={couponCode}
						bagFeeEnabled={bagFeeEnabled}
						openPromo={togglePromoModal}
						setShowOrderNotes={setShowOrderNotePopup}
						checkoutFunc={toggleConfirmModal}
					/>
				</>
			)}
			{global.pos ? <POSNavBar navigation={navigation} screenName={'Cart'} /> : null}
			{!global.kiosk && Platform.OS !== 'web' && cart.length > 0 && !orderPlaced && (
				<NavFooter
					isRestaurantSnoozed={isRestaurantSnoozed}
					loading={loading || (isDelivery && deliveryAmountToSpend !== 0)}
					cartBar={true}
					navigation={navigation}
					price={prices.total}
					onCartPress={cartPress}
				/>
			)}
			{showOrderNotePopup && (
				<NotesInput
					orderNote={orderNote}
					onClose={value => {
						dispatch(setCheckoutState({ orderNote: value }))
						setShowOrderNotePopup(false)
					}}
				/>
			)}

			{cart.length <= 0 && !orderPlaced && <NoItemsInCart navigation={navigation} />}
		</>
	)
}
